import axios from 'axios';
import {ChangeEvent, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {ControllerRenderProps, useForm} from 'react-hook-form';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {HTTP_HOST} from 'shared/consts';
import {unmaskPhone} from 'shared/helpers';
import {useDebounceCallback} from 'shared/lib/debounce/useDebounce';
import {useHookFormMask} from 'use-mask-input';
import {ConfigContext} from '../../../app/App.tsx';

type formValues = {
  login: string;
  phone: string;
  password: string;
  repeatPassword: string;
  city: string;
  firstName: string;
  lastName: string;
  patronymic: string;
  birthday: Date | undefined;
  personalDataConsentAccepted: boolean;
};

export const useRegister = () => {
  const {realm, step} = useParams();
  const defaultTimer = useRef(60);
  const phoneMask = useRef('+7 (999) 999-99-99');
  const lastActualPath = useRef<string>('');

  const navigate = useNavigate();

  const config = useContext(ConfigContext);

  const [sendCodeText, setSendCodeText] = useState<string>('Отправить код повторно');
  const [sendCodeButtonType, setSendCodeButtonType] = useState<
    'ghost' | 'primary' | 'secondary' | 'outline'
  >('ghost');
  const [sendCodeButtonDisabled, setSendCodeButtonDisabled] = useState<boolean>(true);
  const [pinValues, setpinValues] = useState(['', '', '', '']);
  const [timer, setTimer] = useState<number>(0);
  const [showPass, setShowPass] = useState<boolean>(false);
  const [showConfirmPass, setShowConfirmPass] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dadataValues, setDadataValues] = useState<{name: string; value: string}[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [isLoginBtnDisabled, setIsLoginBtnDisabled] = useState<boolean>(true);

  // eslint-disable-next-line
  const emailPattern = useRef<RegExp>(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,8})+$/);

  const externalRedirect = sessionStorage.getItem('externalRedirect');

  const {
    control,
    formState: {errors},
    register,
    setValue,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
  } = useForm<formValues>({
    defaultValues: {
      login: '',
      phone: '',
      password: '',
      repeatPassword: '',
      city: '',
      firstName: '',
      lastName: '',
      patronymic: '',
      birthday: undefined,
      personalDataConsentAccepted: false,
    },
  });

  const registerWithMask = useHookFormMask(register);

  const onLoginComplete = useCallback(async () => {
    const email = getValues().login.trim();

    const res = await axios.get(`/ecpp/users?email=${email}`);

    if (res.data.total > 0) {
      setIsLoginBtnDisabled(true);
      return setError('login', {message: 'Указанная почта уже используется'});
    }
    setIsLoginBtnDisabled(false);
  }, [getValues, setError]);

  const onLoginSubmit = async (data: formValues) => {
    if (!emailPattern.current.test(data.login)) {
      return setError('login', {message: 'Некорректный e-mail'});
    }
    if (!data.login) return setError('login', {message: 'Некорректная почта'});
    setIsLoading(true);

    const response = await axios.post('/ecpp/sms', {email: data.login.trim()}).catch((err) => {
      if (err.response.data.message) {
        const errStr = Array.isArray(err.response.data.message)
          ? err.response.data.message[0]
          : err.response.data.message;

        toast.error(errStr);
      }
    });

    setTimer(defaultTimer.current);
    setIsLoading(false);

    if (response?.status === 200) {
      if (window.ym) window.ym(111273960,'reachGoal','init_registration')
      navigate(`/oauth/realms/${realm}/registration/2`);
    }
  };

  const onBackClick = (path: string) => {
    setTimer(0);
    navigate(path);
  };

  const onPinComplete = async (value: string[]) => {
    setIsLoading(true);
    const resBody = {
      email: getValues().login.trim(),
      code: value.join(''),
    };
    const res = await axios.post('/ecpp/sms/verify', resBody).catch((err) => {
      if (err.response.data.message) {
        const errStr = Array.isArray(err.response.data.message)
          ? err.response.data.message[0]
          : err.response.data.message;

        toast.error(errStr);
      }
    });

    if (res?.status === 200) {
      if (window.ym) window.ym(111273960,'reachGoal','verify_email')
      setTimer(0);
      setpinValues(['', '', '', '']);
      setIsLoading(false);
      navigate(`/oauth/realms/${realm}/registration/3`);
    }
  };

  const reSendPin = async () => {
    await onLoginSubmit(getValues());
  };

  const changeFirstName = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'firstName'>
  ) => {
    field.onChange(event);
  };

  const changeLastName = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'lastName'>
  ) => {
    field.onChange(event);
  };

  const changePatronymic = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'patronymic'>
  ) => {
    field.onChange(event);
  };

  const getCitySuggestions = useDebounceCallback(async () => {
    setDadataValues([]);
    const {city} = getValues();
    if (!city?.trim?.()) {
      setShowSuggestions(false);
      return;
    }

    setShowSuggestions(true);
    const cityRes = await axios
      .post(
        'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address',
        {query: city, from_bound: {value: 'city'}},
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: 'Token ' + config.VITE_DADATA_TOKEN,
          },
        }
      )
      .catch(() => {
        setShowSuggestions(false);
      });

    if (cityRes?.data?.suggestions?.length) {
      const options = cityRes.data.suggestions.map((el: any) => {
        return {
          name: el.value,
          value: el.data.city,
        };
      });
      setDadataValues(options);
    } else {
      setShowSuggestions(false);
    }
  }, 500);

  const changeCity = async (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'city'>
  ) => {
    field.onChange(event);
    getCitySuggestions();
  };

  const onSuggestionClck = (value: string) => {
    setShowSuggestions(false);
    setValue('city', value);
    setDadataValues([]);
  };

  const changePass = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'password'>
  ) => {
    field.onChange(event);
  };

  const changeConfirmPass = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'repeatPassword'>
  ) => {
    field.onChange(event);
  };

  const changeBirthDay = (
    date: Date | undefined,
    field: ControllerRenderProps<formValues, 'birthday'>
  ) => {
    field.onChange(date);
  };

  const onPersDataSubmit = async (data: formValues) => {
    let canNavigate = true;

    if (!data.login || !data.phone || !data.city || !data.firstName || !data.lastName) {
      canNavigate = false;
    }

    if (!data.firstName.trim()) {
      canNavigate = false;
      setError('firstName', {message: 'Введите имя'});
    }
    if (!data.lastName.trim()) {
      canNavigate = false;
      setError('lastName', {message: 'Введите фамилию'});
    }
    if (!data.city.trim()) {
      canNavigate = false;
      setError('city', {message: 'Введите город'});
    }

    if (unmaskPhone(data.phone).length !== 11) {
      canNavigate = false;
      setError('phone', {message: 'Некорректный номер телефона'});
    }

    if (canNavigate) {
      navigate(`/oauth/realms/${realm}/registration/4`);
    }
  };

  const onPassSubmit = async (data: formValues) => {
    if (!data.personalDataConsentAccepted) {
      setError('personalDataConsentAccepted', {
        message: 'Необходимо согласиться на обработку персональных данных',
      });
      return;
    }

    if (data.password !== data.repeatPassword) {
      setError('password', {message: 'Пароли не совпадают'});
      setError('repeatPassword', {message: 'Пароли не совпадают'});
      return;
    }

    const body = {
      login: unmaskPhone(data.phone),
      password: data.password.trim(),
      city: data.city.trim(),
      email: data.login.trim(),
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      patronymic: data.patronymic.trim(),
      birthday: data.birthday?.toISOString(),
      personalDataConsentAccepted: data.personalDataConsentAccepted,
    };

    setIsLoading(true);

    const resParams = {
      headers: {
        authorization: `Basic ${btoa('web:')}`,
      },
    };
    const response = await axios
      .post(`${HTTP_HOST}/oauth/realms/${realm}/registration`, body, resParams)
      .catch((err) => {
        if (err.response.data.fields?.length) {
          for (let index = 0; index < err.response.data.fields.length; index++) {
            const element = err.response.data.fields[index];
            toast.error(element.errorMessage);
          }
        } else {
          if (err.response.data.message) {
            if (Array.isArray(err.response.data.message)) {
              for (let index = 0; index < err.response.data.message.length; index++) {
                const error = err.response.data.message[index];
                toast.error(error);
              }
            } else {
              toast.error(err.response.data.message);
            }
          }
        }
      });

    if (response?.data?.code) {
      const params = {
        grant_type: 'code',
        scope: 'openid',
        code: response?.data?.code,
        client_id: 'web',
        redirect_uri: `${window.location.origin || config.VITE_AUTH_HOST || import.meta.env.VITE_AUTH_HOST}/`,
        auth_mode: 'cookie',
      };

      const path = `/ecpp/oauth/realms/${realm}/token?${new URLSearchParams(params).toString()}`;

      const tokenRes = await axios.post(path, undefined, resParams).catch((err) => {
        if (err.response.data.message) {
          if (Array.isArray(err.response.data.message)) {
            for (let index = 0; index < err.response.data.message.length; index++) {
              const error = err.response.data.message[index];
              toast.error(error);
            }
          } else {
            toast.error(err.response.data.message);
          }
        }
      });

      if (tokenRes?.data) {
        const target = externalRedirect
          ? `${externalRedirect}${externalRedirect.includes('?') ? '&' : '?'}code=${response.data.code}`
          : '/';
        
        if (window.ym) window.ym(111273960,'reachGoal','complete_registration')
        window.location.replace(target);
      }
    }

    setIsLoading(false);
  };

  const passValidate = () => {
    const {password, repeatPassword} = getValues();

    if (password !== repeatPassword) {
      return 'Пароли не совпадают';
    }

    clearErrors();
    return true;
  };

  useEffect(() => {
    if (!step || !getValues().login) {
      setTimer(0);
      navigate(`/oauth/realms/${realm}/registration/1`);
    }
    if (step === '1' || step === '2' || step === '3' || step === '4') {
      lastActualPath.current = window.location.pathname;
    } else if (lastActualPath.current?.trim()) {
      navigate(lastActualPath.current);
    } else {
      navigate(`/oauth/realms/${realm || 'main'}/registration/1`);
    }

    const firstInputOnPage = document.querySelector('input');
    firstInputOnPage?.focus();
  }, [step, realm]);

  useEffect(() => {
    if (timer > 0) {
      setSendCodeButtonDisabled(true);
      setSendCodeButtonType('ghost');
      setSendCodeText(`Отправить код повторно (${timer} сек.)`);
      setTimeout(() => {
        setTimer(timer - 1);
      }, 1000);
    } else {
      setSendCodeButtonDisabled(false);
      setSendCodeButtonType('outline');
      setSendCodeText(`Отправить код повторно`);
    }
  }, [timer, setTimer, setSendCodeText]);

  useEffect(() => {
    if (pinValues.filter((el) => el.trim()).length === pinValues.length) onPinComplete(pinValues);
  }, [pinValues]);

  return {
    control,
    realm,
    step,
    sendCodeText,
    sendCodeButtonType,
    sendCodeButtonDisabled,
    phoneMask,
    errors,
    pinValues,
    showPass,
    showConfirmPass,
    isLoading,
    dadataValues,
    showSuggestions,
    isLoginBtnDisabled,
    emailPattern,
    registerWithMask,
    register,
    onLoginSubmit,
    onBackClick,
    handleSubmit,
    setValue,
    reSendPin,
    setpinValues,
    changeFirstName,
    changeLastName,
    changePatronymic,
    changeCity,
    onPersDataSubmit,
    changeBirthDay,
    onPassSubmit,
    setShowPass,
    setShowConfirmPass,
    changePass,
    changeConfirmPass,
    clearErrors,
    passValidate,
    onSuggestionClck,
    onLoginComplete,
    setIsLoginBtnDisabled,
  };
};
